import { Formik, useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import moment from 'moment';
import { FiCheckCircle, FiEye } from 'react-icons/fi';
import { RiDeleteBinLine, RiMoneyDollarCircleLine } from 'react-icons/ri';
import Button from '../../components/button';
import CustomModal from '../../components/CustomModal';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import AttachmentViewerModal from '../../components/modals/AttachmentViewerModal';
import ConfirmDeleteModal from '../../components/modals/ConfirmDeleteModal';
import StatusModal from '../../components/modals/StatusModal';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import {
    createFuelLog,
    deleteFuelLogs,
    getFuelStations,
} from '../../store/Slices/FuelSlice';
import { getTripDetails } from '../../store/Slices/TripSlice';
import { formatDate } from '../../utils/helper';
import { errorNotify, successNotify } from '../../utils/toast';

const fuelTypeOptions = [
    { label: 'Diesel', value: 'diesel' },
    { label: 'Petrol', value: 'petrol' },
    { label: 'CNG', value: 'cng' },
    { label: 'Electric', value: 'electric' },
    { label: 'Hybrid', value: 'hybrid' },
    { label: 'Octane', value: 'octane' },
];

const getFiniteNumber = (...values) => {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
};

const extractCurrentFuelRate = (payload = {}) =>
    getFiniteNumber(
        payload,
        payload?.data,
        payload?.current_rate,
        payload?.current_fuel_rate,
        payload?.fuel_rate,
        payload?.rate,
        payload?.price
    );

const buildFuelLogSchema = (maxQty = null) => {
    let qtySchema = Yup.number()
        .typeError('Liters must be a number')
        .positive('Liters must be greater than 0')
        .required('Liters Claimed is required');

    if (maxQty !== null && Number.isFinite(maxQty)) {
        if (maxQty <= 0) {
            qtySchema = qtySchema.max(0, 'No remaining fuel allocation for this trip');
        } else {
            qtySchema = qtySchema.max(maxQty, `Cannot exceed remaining allocated fuel (${maxQty} ltrs)`);
        }
    }

    return Yup.object().shape({
        vendor_id: Yup.string().required('Filling Station is required'),
        fuelType: Yup.string().required('Fuel Type is required'),
        filled_at: Yup.string().required('Refuel Date is required'),
        quantity: qtySchema,
        total_amount: Yup.number()
            .typeError('Total amount must be a number')
            .positive('Total amount must be greater than 0')
            .required('Total Cost Paid is required'),
    });
};

const mapUploadedFilesForPayload = (files = []) =>
    (Array.isArray(files) ? files : [])
        .map((file) => {
            if (typeof file === 'string') return file;
            return file?.fileName || file?.file_name || file?.file_path || file?.url || '';
        })
        .filter(Boolean);

// Fuel claim images must be sent as absolute URLs; FileUpload's own entries only
// keep the relative storage path, so prefix the files base URL when it's missing.
const mapUploadedFilesToUrls = (files = []) =>
    mapUploadedFilesForPayload(files).map((path) =>
        /^https?:\/\//i.test(path)
            ? path
            : `${String(process.env.REACT_APP_API_FILES_URL || '').replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`
    );

const formatStatusLabel = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return '--';

    return normalized
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

// Same pill shape as the ambient `.service_line_items_table td span` style
// (padding: 5px 12px, border-radius: 7px) — only the fill color changes per status.
const getStatusBadgeColors = (value) => {
    const normalized = String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '-');

    const statusColors = {
        approved: { background: '#dcfce7', color: '#15803d' },
        verified: { background: '#dcfce7', color: '#15803d' },
        completed: { background: '#dcfce7', color: '#15803d' },
        issued: { background: '#dbeafe', color: '#1d4ed8' },
        claimed: { background: '#e0e7ff', color: '#4338ca' },
        pending: { background: '#fef3c7', color: '#b45309' },
        unverified: { background: '#ffedd5', color: '#c2410c' },
        rejected: { background: '#fee2e2', color: '#dc2626' },
        correction: { background: '#fee2e2', color: '#dc2626' },
    };

    return statusColors[normalized] || { background: '#F2F2F2', color: '#666666' };
};

const FuelLogExpectedCostSync = ({ setCurrentFuelRateLoading }) => {
    const { values, setFieldValue } = useFormikContext();
    const [currentRate, setCurrentRate] = useState(null);

    const hasRequiredSelections = Boolean(
        values.vendor_id && values.fuelType && values.filled_at
    );

    useEffect(() => {
        let isActive = true;

        if (!hasRequiredSelections) {
            setCurrentRate(null);
            setCurrentFuelRateLoading(false);
            setFieldValue('expected_total_fuel_cost', '');
            setFieldValue('fuel_price', '');
            return;
        }

        setCurrentFuelRateLoading(true);
        apiService
            .getCurrentFuelRate({
                fuel_type: values.fuelType,
                fuel_station_id: Number(values.vendor_id),
                changed_at: values.filled_at,
            })
            .then((response) => {
                if (!isActive) return;
                const responseData = response?.data ?? response ?? {};
                const nextRate = extractCurrentFuelRate(responseData);
                setCurrentRate(nextRate);
                setFieldValue('fuel_price', nextRate !== null ? nextRate : '');

                const qty = Number(values.quantity);
                if (nextRate !== null && Number.isFinite(qty) && qty > 0) {
                    setFieldValue('expected_total_fuel_cost', (nextRate * qty).toFixed(2));
                } else {
                    setFieldValue('expected_total_fuel_cost', '');
                }
            })
            .catch(() => {
                if (!isActive) return;
                setCurrentRate(null);
                setFieldValue('expected_total_fuel_cost', '');
                setFieldValue('fuel_price', '');
            })
            .finally(() => {
                if (isActive) setCurrentFuelRateLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [hasRequiredSelections, values.vendor_id, values.fuelType, values.filled_at, setCurrentFuelRateLoading, setFieldValue]);

    useEffect(() => {
        const qty = Number(values.quantity);
        if (currentRate !== null && Number.isFinite(qty) && qty > 0) {
            setFieldValue('expected_total_fuel_cost', (currentRate * qty).toFixed(2));
        } else {
            setFieldValue('expected_total_fuel_cost', '');
        }
    }, [values.quantity, currentRate, setFieldValue]);

    return null;
};

const TripFuelLogs = ({
    tripFuelLogs = [],
    trip_id,
    tripIdDisplay,
    vehicle_id,
    vehicle_label,
    fuel_type = 'diesel',
    trip_start_at,
    isTripCompleted = false,
    canAddFuelLog = true,
    canEditFuelLog = true,
    canDeleteFuelLog = true,
    allocated_fuel,
    total_filled_fuel,
}) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const realTripId = trip_id || id;

    const remainingFuel = useMemo(() => {
        const allocated = Number(allocated_fuel);
        const filled = Number(total_filled_fuel);
        if (!Number.isFinite(allocated)) return null;
        return Math.max(0, allocated - (Number.isFinite(filled) ? filled : 0));
    }, [allocated_fuel, total_filled_fuel]);

    const activeFuelLogSchema = useMemo(() => buildFuelLogSchema(remainingFuel), [remainingFuel]);

    const [showAddForm, setShowAddForm] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [attachmentPreviewFiles, setAttachmentPreviewFiles] = useState([]);
    const [currentFuelRateLoading, setCurrentFuelRateLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [selectedVerifyId, setSelectedVerifyId] = useState(null);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [claimModalOpen, setClaimModalOpen] = useState(false);
    const [selectedClaimLog, setSelectedClaimLog] = useState(null);
    const [claimImages, setClaimImages] = useState([]);
    const [claimLoading, setClaimLoading] = useState(false);
    const scrollPositionRef = useRef(0);

    const handleOpenAttachmentViewer = (files) => {
        setAttachmentPreviewFiles(Array.isArray(files) ? files : (files ? [files] : []));
    };

    const handleDelete = (itemId, e) => {
        handleOpenDeleteModal(itemId, e);
    };

    useEffect(() => {
        if (deleteModalOpen || verifyModalOpen || claimModalOpen) {
            const currentScroll = scrollPositionRef.current;
            requestAnimationFrame(() => {
                window.scrollTo({ top: currentScroll, behavior: 'instant' });
            });
        }
    }, [deleteModalOpen, verifyModalOpen, claimModalOpen]);

    const { getFuelStationsData, getFuelStationsLoading, createFuelLogLoading } = useSelector(
        (state) => state.fuel
    );
    const { tripDetailsData } = useSelector((state) => state.trips || {});

    const effectiveTripStart = trip_start_at ||
        tripDetailsData?.actual_start_at ||
        tripDetailsData?.started_at ||
        tripDetailsData?.dispatch_at ||
        tripDetailsData?.start_at ||
        tripDetailsData?.created_at;

    const minRefuelDate = effectiveTripStart ? moment(effectiveTripStart).format('YYYY-MM-DD') : null;
    const tripStartDisplay = effectiveTripStart ? moment(effectiveTripStart).format('DD/MM/YYYY, HH:mm') : null;

    useEffect(() => {
        dispatch(getFuelStations());
    }, [dispatch]);

    const resolvedFuelType = useMemo(() => {
        const normalized = String(fuel_type || '').trim().toLowerCase();
        const matched = fuelTypeOptions.find((opt) => opt.value === normalized);
        return matched ? matched.value : 'diesel';
    }, [fuel_type]);

    const initialValues = {
        trip_id: tripIdDisplay || (realTripId ? `TR-${realTripId}` : ''),
        vehicle_id: vehicle_label || (vehicle_id ? `Vehicle #${vehicle_id}` : ''),
        vendor_id: '',
        fuelType: resolvedFuelType,
        filled_at: (minRefuelDate && moment().isBefore(moment(minRefuelDate), 'day'))
            ? minRefuelDate
            : moment().format('YYYY-MM-DD'),
        quantity: '',
        expected_total_fuel_cost: '',
        total_amount: '',
        fuel_price: '',
    };

    const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!canAddFuelLog) {
            errorNotify('You do not have permission to add fuel logs.');
            setSubmitting(false);
            return;
        }

        try {
            const resolvedVehicleNumericId = Number(vehicle_id);
            const resolvedTripNumericId = Number(realTripId);

            if (!resolvedVehicleNumericId) {
                errorNotify('Vehicle ID is missing for this trip.');
                setSubmitting(false);
                return;
            }

            if (effectiveTripStart && values.filled_at) {
                const selectedDate = moment(values.filled_at, 'YYYY-MM-DD');
                const tripStartDate = moment(effectiveTripStart).startOf('day');
                if (selectedDate.isBefore(tripStartDate)) {
                    errorNotify(`Refuel date cannot be earlier than trip start date (${tripStartDisplay || minRefuelDate})`);
                    setSubmitting(false);
                    return;
                }
            }

            const payload = {
                trip_id: resolvedTripNumericId,
                vehicle_id: resolvedVehicleNumericId,
                fuel_station_id: parseInt(values.vendor_id),
                refuel_at: values.filled_at,
                fuel_type: String(values.fuelType || '').trim(),
                claimed_fueled: parseFloat(values.quantity),
                expected_total_fuel_cost: values.expected_total_fuel_cost !== ''
                    ? parseFloat(values.expected_total_fuel_cost)
                    : null,
                total_fuel_cost: parseFloat(values.total_amount),
                fuel_price: values.fuel_price !== '' ? parseFloat(values.fuel_price) : null,
                images: mapUploadedFilesForPayload(uploadedFiles),
            };

            await dispatch(createFuelLog(payload)).unwrap();
            successNotify('Fuel log added successfully');

            dispatch(getTripDetails(realTripId));

            resetForm();
            setShowAddForm(false);
            setUploadedFiles([]);
        } catch (error) {
            errorNotify(error?.message || 'Failed to add fuel log');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDeleteModal = (itemId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (isTripCompleted || !canDeleteFuelLog) return;
        scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop || 0;
        setSelectedDeleteId(itemId);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedDeleteId) return;
        setDeleteLoading(true);
        try {
            await dispatch(deleteFuelLogs(selectedDeleteId)).unwrap();
            successNotify('Fuel log deleted successfully');
            setDeleteModalOpen(false);
            setSelectedDeleteId(null);
            dispatch(getTripDetails(realTripId));
        } catch (error) {
            errorNotify(error?.message || 'Failed to delete fuel log');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCancel = () => {
        setShowAddForm(false);
        setUploadedFiles([]);
    };

    const handleOpenVerifyModal = (fuelLogId, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop || 0;
        setSelectedVerifyId(fuelLogId);
        setVerifyModalOpen(true);
    };

    const handleConfirmVerify = async () => {
        const resolvedTripNumericId = Number(realTripId || trip_id);
        const resolvedFuelLogId = Number(selectedVerifyId);

        if (!resolvedFuelLogId) {
            errorNotify('Fuel Log ID is missing');
            return;
        }

        if (!resolvedTripNumericId) {
            errorNotify('Trip ID is missing');
            return;
        }

        setVerifyLoading(true);
        try {
            const payload = {
                fuel_log_id: resolvedFuelLogId,
                trip_id: resolvedTripNumericId,
            };
            const response = await apiService.verifyFuelLog(payload);
            successNotify(response?.message || 'Fuel log verified successfully');
            setVerifyModalOpen(false);
            setSelectedVerifyId(null);
            dispatch(getTripDetails(realTripId));
        } catch (error) {
            console.error('Error verifying fuel log:', error);
            errorNotify(typeof error === 'string' ? error : error?.message || 'Failed to verify fuel log');
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleOpenClaimModal = (item, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop || 0;
        setSelectedClaimLog(item);
        setClaimImages([]);
        setClaimModalOpen(true);
    };

    const handleCloseClaimModal = () => {
        if (claimLoading) return;
        setClaimModalOpen(false);
        setSelectedClaimLog(null);
        setClaimImages([]);
    };

    const handleSubmitClaim = async (values, { setSubmitting }) => {
        const resolvedFuelLogId = Number(selectedClaimLog?.id);
        const resolvedTripNumericId = Number(realTripId || trip_id);

        if (!resolvedFuelLogId) {
            errorNotify('Fuel Log ID is missing');
            setSubmitting(false);
            return;
        }

        if (!resolvedTripNumericId) {
            errorNotify('Trip ID is missing');
            setSubmitting(false);
            return;
        }

        setClaimLoading(true);
        try {
            const payload = {
                fuel_log_id: resolvedFuelLogId,
                trip_id: resolvedTripNumericId,
                claimed_fueled: parseFloat(values.claimed_fueled),
                images: mapUploadedFilesToUrls(claimImages),
            };
            await apiService.createFuelClaim(payload);
            successNotify('Fuel claim submitted successfully');
            handleCloseClaimModal();
            dispatch(getTripDetails(realTripId));
        } catch (error) {
            errorNotify(typeof error === 'string' ? error : error?.message || 'Failed to submit fuel claim');
        } finally {
            setClaimLoading(false);
            setSubmitting(false);
        }
    };

    const openAttachmentViewer = (files = []) => {
        const nextFiles = Array.isArray(files) ? files : (files ? [files] : []);
        if (!nextFiles.length) {
            errorNotify('No attachments found');
            return;
        }
        setAttachmentPreviewFiles(nextFiles);
    };

    const safeLogs = Array.isArray(tripFuelLogs) ? tripFuelLogs : [];

    return (
        <div className="service_line_items_main" style={{ border: 'none', padding: '0px' }}>
            {/* Fuel Logs Table */}
            <div className="service_line_items_table" style={{ marginBottom: '20px' }}>
            {/* Desktop Table View */}
            <div className="desktop_table_view">
                
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Station / Location</th>
                            <th>Fuel Type</th>
                            <th>Fuel (Ltrs)</th>
                            <th>Cost (PKR)</th>
                            <th>Status</th>
                            <th>Refuel Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {safeLogs.length > 0 ? (
                            safeLogs.map((item, index) => {
                                const stationName =
                                    item?.fuel_station?.fuel_station_name ||
                                    item?.fuel_station?.name ||
                                    item?.station_name ||
                                    item?.location ||
                                    '--';
                                const liters = item?.claimed_fueled || item?.quantity || item?.liters || '--';
                                const cost = item?.total_fuel_cost || item?.total_amount || item?.cost || '0';
                                const status = item?.status || item?.fuel_log_status || item?.approval_status || '';
                                const statusColors = getStatusBadgeColors(status);
                                const normalizedStatus = String(status || '').trim().toLowerCase();
                                const isVerified = ['verified', 'approved'].includes(normalizedStatus);
                                const isIssuedUnclaimed = normalizedStatus === 'issued';
                                const dateStr = item?.refuel_at || item?.filled_at || item?.created_at;
                                const files = item?.files || item?.images || (item?.attachment ? [item.attachment] : []);
                                const filesCount = Array.isArray(files) ? files.length : 0;

                                return (
                                    <tr key={item?.id || index}>
                                        <td style={{ fontWeight: '600', color: '#1e293b' }}>{stationName}</td>
                                        <td>
                                            <span style={{ textTransform: 'capitalize' }}>
                                                {item?.fuel_type || '--'}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: '600' }}>{liters}</td>
                                        <td style={{ fontWeight: '700', color: '#2563eb' }}>
                                            {Number(cost).toLocaleString()}
                                        </td>
                                        <td>
                                            <span
                                                style={{
                                                    backgroundColor: statusColors.background,
                                                    color: statusColors.color,
                                                }}
                                            >
                                                {formatStatusLabel(status)}
                                            </span>
                                        </td>
                                        <td>{formatDate(dateStr)}</td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-center align-items-center">
                                                {isIssuedUnclaimed ? (
                                                    <RiMoneyDollarCircleLine
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#2563eb',
                                                            fontSize: '19px',
                                                        }}
                                                        onClick={(e) => handleOpenClaimModal(item, e)}
                                                        title="Claim fuel"
                                                    />
                                                ) : (
                                                    <FiCheckCircle
                                                        style={{
                                                            cursor: isVerified ? 'not-allowed' : 'pointer',
                                                            color: isVerified ? '#94a3b8' : '#16a34a',
                                                            fontSize: '17px',
                                                            opacity: isVerified ? 0.5 : 1,
                                                        }}
                                                        onClick={isVerified ? undefined : (e) => handleOpenVerifyModal(item?.id, e)}
                                                        title={isVerified ? 'Already Verified' : 'Approve / Verify fuel log'}
                                                    />
                                                )}
                                                {!isTripCompleted && canDeleteFuelLog && (
                                                    <RiDeleteBinLine
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#F44336',
                                                            fontSize: '16px',
                                                        }}
                                                        onClick={(e) => handleOpenDeleteModal(item?.id, e)}
                                                        title="Delete fuel log"
                                                    />
                                                )}
                                                {filesCount > 0 && (
                                                    <button
                                                        type="button"
                                                        className="trip_attachment_view_btn"
                                                        onClick={() => openAttachmentViewer(files)}
                                                        title="View attachments"
                                                    >
                                                        <FiEye /> {filesCount}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" className="text-center" style={{ padding: '20px', color: '#888' }}>
                                    No fuel logs found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Mobile Cards View */}
            <div className="mobile_cards_view">
                {safeLogs.length > 0 ? (
                    safeLogs.map((item, index) => {
                        const stationName =
                            item?.fuel_station?.fuel_station_name ||
                            item?.fuel_station?.name ||
                            item?.station_name ||
                            item?.location ||
                            '--';
                        const liters = item?.claimed_fueled || item?.quantity || item?.liters || '--';
                        const cost = item?.total_fuel_cost || item?.total_amount || item?.cost || '0';
                        const status = item?.status || item?.fuel_log_status || item?.approval_status || '';
                        const statusColors = getStatusBadgeColors(status);
                        const normalizedStatus = String(status || '').trim().toLowerCase();
                        const isVerified = ['verified', 'approved'].includes(normalizedStatus);
                        const isIssuedUnclaimed = normalizedStatus === 'issued';
                        const dateStr = item?.refuel_at || item?.filled_at || item?.created_at;
                        const files = item?.files || item?.images || (item?.attachment ? [item.attachment] : []);
                        const filesCount = Array.isArray(files) ? files.length : 0;

                        return (
                            <div key={item?.id || index} className="mobile_data_card">
                                <div className="mobile_card_header">
                                    <div className="mobile_card_id">{stationName}</div>
                                    <span
                                        style={{
                                            backgroundColor: statusColors.background,
                                            color: statusColors.color,
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {formatStatusLabel(status)}
                                    </span>
                                </div>
                                <div className="mobile_card_row">
                                    <div className="mobile_card_field">
                                        <span className="mobile_card_label">Fuel Type</span>
                                        <span className="mobile_card_value" style={{ textTransform: 'capitalize' }}>
                                            {item?.fuel_type || '--'}
                                        </span>
                                    </div>
                                    <div className="mobile_card_field">
                                        <span className="mobile_card_label">Liters</span>
                                        <span className="mobile_card_value" style={{ fontWeight: '600' }}>{liters} L</span>
                                    </div>
                                    <div className="mobile_card_field">
                                        <span className="mobile_card_label">Cost (PKR)</span>
                                        <span className="mobile_card_value" style={{ fontWeight: '700', color: '#2563eb' }}>
                                            {Number(cost).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="mobile_card_field">
                                        <span className="mobile_card_label">Refuel Date</span>
                                        <span className="mobile_card_value">{formatDate(dateStr)}</span>
                                    </div>
                                </div>
                                <div
                                    className="mobile_card_footer"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '12px',
                                        marginTop: '10px',
                                        paddingTop: '8px',
                                        borderTop: '1px solid #f1f5f9',
                                    }}
                                >
                                    {isIssuedUnclaimed && (
                                        <RiMoneyDollarCircleLine
                                            style={{ cursor: 'pointer', color: '#2563eb', fontSize: '20px' }}
                                            onClick={(e) => handleOpenClaimModal(item, e)}
                                            title="Claim fuel"
                                        />
                                    )}
                                    {!isVerified && !isTripCompleted && canEditFuelLog && !isIssuedUnclaimed && (
                                        <FiCheckCircle
                                            style={{ cursor: 'pointer', color: '#16a34a', fontSize: '18px' }}
                                            onClick={() => handleOpenVerifyModal(item?.id)}
                                            title="Verify fuel log"
                                        />
                                    )}
                                    {filesCount > 0 && (
                                        <FiEye
                                            style={{ cursor: 'pointer', color: '#475569', fontSize: '18px' }}
                                            onClick={() => handleOpenAttachmentViewer(files)}
                                            title="View attachments"
                                        />
                                    )}
                                    {!isTripCompleted && canDeleteFuelLog && (
                                        <RiDeleteBinLine
                                            style={{ cursor: 'pointer', color: '#dc2626', fontSize: '18px' }}
                                            onClick={() => handleDelete(item?.id)}
                                            title="Delete fuel log"
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#888', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        No fuel logs found
                    </div>
                )}
            </div>
        </div>

            {/* Add Fuel Log Button */}
            {!showAddForm && !isTripCompleted && canAddFuelLog && (
                <button
                    className="add_issue_btn"
                    type="button"
                    onClick={() => {
                        setShowAddForm(true);
                        setUploadedFiles([]);
                    }}
                >
                    + Add Fuel Log
                </button>
            )}

            {/* Add Fuel Log Formik Form */}
            {showAddForm && (
                <div className="add_petty_cash" style={{ marginTop: '16px' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={activeFuelLogSchema}
                        validate={(values) => {
                            const errs = {};
                            if (effectiveTripStart && values.filled_at) {
                                const selectedDate = moment(values.filled_at, 'YYYY-MM-DD');
                                const tripStartDate = moment(effectiveTripStart).startOf('day');
                                if (selectedDate.isBefore(tripStartDate)) {
                                    errs.filled_at = `Refuel date cannot be earlier than trip start date (${tripStartDisplay || minRefuelDate})`;
                                }
                            }
                            return errs;
                        }}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                            handleSubmit,
                            isSubmitting,
                        }) => {
                            const stationOptions = (getFuelStationsData || []).map((st) => ({
                                label: st.label || st.name || st.fuel_station_name,
                                value: st.value || st.id,
                            }));

                            return (
                                <div>
                                    <FuelLogExpectedCostSync
                                        setCurrentFuelRateLoading={setCurrentFuelRateLoading}
                                    />
                                    <h4>Add Fuel Log</h4>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Input
                                                label="Trip ID"
                                                name="trip_id"
                                                value={values.trip_id}
                                                disabled={true}
                                                required
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Input
                                                label="Vehicle"
                                                name="vehicle_id"
                                                value={values.vehicle_id}
                                                disabled={true}
                                                required
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Select
                                                label="Filling Station"
                                                placeholder="Select Filling Station"
                                                options={stationOptions}
                                                loading={getFuelStationsLoading}
                                                name="vendor_id"
                                                value={stationOptions.find(
                                                    (opt) => String(opt.value) === String(values.vendor_id)
                                                ) || null}
                                                onChange={(opt) => setFieldValue('vendor_id', opt ? opt.value : '')}
                                                onBlur={handleBlur}
                                                error={errors.vendor_id}
                                                touched={touched.vendor_id}
                                                required
                                                isSearch={true}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Select
                                                label="Fuel Type"
                                                placeholder="Select Fuel Type"
                                                options={fuelTypeOptions}
                                                name="fuelType"
                                                value={fuelTypeOptions.find(
                                                    (opt) => opt.value === values.fuelType
                                                ) || null}
                                                onChange={(opt) => setFieldValue('fuelType', opt ? opt.value : '')}
                                                onBlur={handleBlur}
                                                error={errors.fuelType}
                                                touched={touched.fuelType}
                                                required
                                                isSearch={false}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Input
                                                label="Refuel Date"
                                                type="date"
                                                name="filled_at"
                                                placeholder="YYYY-MM-DD"
                                                value={values.filled_at}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.filled_at}
                                                touched={touched.filled_at}
                                                min={minRefuelDate}
                                                minValue={minRefuelDate}
                                                required
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Input
                                                label={`Liters Claimed (L)${remainingFuel !== null ? ` (Max: ${remainingFuel} ltrs)` : ''}`}
                                                placeholder="0.00"
                                                type="number"
                                                name="quantity"
                                                value={values.quantity}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.quantity}
                                                touched={touched.quantity}
                                                required
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Input
                                                label="Expected Total Fuel Cost"
                                                placeholder={
                                                    currentFuelRateLoading
                                                        ? 'Fetching fuel rate...'
                                                        : 'Auto calculated'
                                                }
                                                type="number"
                                                name="expected_total_fuel_cost"
                                                value={values.expected_total_fuel_cost}
                                                disabled={true}
                                                required={false}
                                                showExtraText
                                                extraText="PKR"
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Input
                                                label="Total Cost Paid"
                                                placeholder="00"
                                                type="number"
                                                name="total_amount"
                                                value={values.total_amount}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.total_amount}
                                                touched={touched.total_amount}
                                                required
                                                showExtraText
                                                extraText="PKR"
                                            />
                                        </Col>

                                        <Col md={12}>
                                            <div className="photo_document_section" style={{ border: 'none', padding: '0', marginTop: '0' }}>
                                                <label style={{ fontSize: '13px', fontWeight: '500', marginBottom: '6px', display: 'block' }}>
                                                    Attachments
                                                </label>
                                                <FileUpload
                                                    destination="fuel-logs"
                                                    files={uploadedFiles}
                                                    setFiles={setUploadedFiles}
                                                    maxFileSize={10}
                                                    tripId={realTripId}
                                                />
                                            </div>
                                        </Col>

                                        <Col md={12} className="mt-3">
                                            <div className="d-flex gap-3">
                                                <Button
                                                    cancelBtn={handleCancel}
                                                    loading={isSubmitting || createFuelLogLoading}
                                                    disabled={isSubmitting || createFuelLogLoading || currentFuelRateLoading}
                                                    submitBtn={handleSubmit}
                                                    submitText="Save"
                                                    shouldRow={false}
                                                    buttonExtraCss="w-100"
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        }}
                    </Formik>
                </div>
            )}

            {/* Attachment Viewer Modal */}
            <AttachmentViewerModal
                show={attachmentPreviewFiles.length > 0}
                files={attachmentPreviewFiles}
                heading="Fuel Log Attachments"
                handleClose={() => setAttachmentPreviewFiles([])}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                show={deleteModalOpen}
                onClose={() => {
                    if (!deleteLoading) {
                        setDeleteModalOpen(false);
                        setSelectedDeleteId(null);
                    }
                }}
                onConfirm={handleConfirmDelete}
                loading={deleteLoading}
                title="Are you sure you want to delete this fuel log?"
                description="This action cannot be undone."
                confirmText="Delete"
            />

            {/* Verify / Approve Confirmation Modal */}
            <StatusModal
                open={verifyModalOpen}
                onClose={() => {
                    if (!verifyLoading) {
                        setVerifyModalOpen(false);
                        setSelectedVerifyId(null);
                    }
                }}
                onConfirm={handleConfirmVerify}
                status="verified"
                loading={verifyLoading}
                titleText="Are you sure you want to verify this fuel log?"
                description="This action will verify and approve the selected fuel log."
                confirmText="Verify"
                centered
            />

            {/* Fuel Claim Modal */}
            <CustomModal
                show={claimModalOpen}
                handleClose={handleCloseClaimModal}
                heading="Claim Fuel"
                size="md"
            >
                <Formik
                    initialValues={{ claimed_fueled: '' }}
                    enableReinitialize={true}
                    validate={(values) => {
                        const validationErrors = {};
                        const rawClaimed = values.claimed_fueled;

                        if (rawClaimed === '' || rawClaimed === null || rawClaimed === undefined) {
                            validationErrors.claimed_fueled = 'Claimed fuel is required';
                        } else {
                            const parsedClaimed = Number(rawClaimed);
                            if (!Number.isFinite(parsedClaimed)) {
                                validationErrors.claimed_fueled = 'Claimed fuel must be a number';
                            } else if (parsedClaimed <= 0) {
                                validationErrors.claimed_fueled = 'Claimed fuel must be greater than 0';
                            }
                        }

                        if (!claimImages.length) {
                            validationErrors.images = 'At least one receipt image is required';
                        }

                        return validationErrors;
                    }}
                    onSubmit={handleSubmitClaim}
                >
                    {({ values, errors, touched, submitCount, validateForm, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                        <Row className="g-3">
                            <Col md={12}>
                                <Input
                                    label="Claimed Fuel (L)"
                                    placeholder="0.00"
                                    type="number"
                                    name="claimed_fueled"
                                    value={values.claimed_fueled}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.claimed_fueled}
                                    touched={touched.claimed_fueled}
                                    required
                                />
                            </Col>

                            <Col md={12}>
                                <div className="photo_document_section" style={{ border: 'none', padding: '0', marginTop: '0' }}>
                                    <label style={{ display: 'flex', gap: '5px', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                                        Receipt Images
                                        <span style={{ color: 'red', fontSize: '13px' }}>*</span>
                                    </label>
                                    <FileUpload
                                        destination="fuel-claims"
                                        files={claimImages}
                                        setFiles={(next) => {
                                            setClaimImages(next);
                                            setTimeout(validateForm, 0);
                                        }}
                                        maxFileSize={10}
                                        tripId={realTripId}
                                    />
                                    {submitCount > 0 && errors.images && (
                                        <div className="error-message" style={{ color: '#ff0000de', fontSize: '12px', marginTop: '3px', marginLeft: '5px' }}>
                                            {errors.images}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            <Col md={12} className="mt-3">
                                <div className="d-flex gap-3">
                                    <Button
                                        cancelBtn={handleCloseClaimModal}
                                        loading={isSubmitting || claimLoading}
                                        disabled={isSubmitting || claimLoading}
                                        submitBtn={handleSubmit}
                                        submitText="Submit Claim"
                                        shouldRow={false}
                                        buttonExtraCss="w-100"
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                </Formik>
            </CustomModal>
        </div>
    );
};

export default TripFuelLogs;
